import { useCallback, useEffect, useRef, useState } from 'react';
import BlogCard from '../common/BlogCard';

// How long each card stays in view before auto-advancing.
const AUTOPLAY_DELAY_MS = 4000;

/**
 * Horizontally sliding row of up to six featured cards.
 *
 * Plain scroll-snap, not a JS carousel library: the track is a native
 * overflow-x scroller so touch/trackpad swipe and keyboard (Tab + arrow keys
 * once a card is focused) work for free. The prev/next buttons and dots are a
 * thin convenience on top — they scroll to a card / read scroll position, they
 * do not own the "real" state, so there is nothing to get out of sync with
 * what the reader already scrolled to by hand.
 *
 * Auto-advances every few seconds, and pauses the moment the reader touches
 * it (hover, focus, or an in-progress touch drag) so it never fights a manual
 * swipe or yanks the row out from under a click.
 *
 * @param {object[]} posts  up to six posts, already capped by the caller (see
 *                          HomePage) — this component just renders whatever
 *                          it is given.
 * @param {string}   label  accessible name for the scroller region
 */
function FeaturedCarousel({ posts = [], label = 'Featured articles' }) {
  const trackRef = useRef(null);
  const resumeTimeoutRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  // Bumped on every MANUAL navigation (arrow, dot, or a hand swipe caught by
  // the scroll listener below). Included as an autoplay effect dependency so
  // the interval restarts from zero after one of those, instead of possibly
  // firing a moment later and re-advancing past a card the reader just chose.
  const [manualNavCount, setManualNavCount] = useState(0);

  // Keeps the dots in sync with a manual swipe/scroll, not just clicks on the
  // dots/arrows themselves. Reads which card's start edge is closest to the
  // track's own left edge, which is exactly what scroll-snap already aligned.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    let raf = null;
    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const items = track.querySelectorAll('.featured-carousel-item');
        const trackLeft = track.getBoundingClientRect().left;
        let closest = 0;
        let closestDist = Infinity;
        items.forEach((item, i) => {
          const dist = Math.abs(item.getBoundingClientRect().left - trackLeft);
          if (dist < closestDist) {
            closestDist = dist;
            closest = i;
          }
        });
        setActiveIndex((prev) => {
          if (prev !== closest) setManualNavCount((n) => n + 1);
          return closest;
        });
      });
    };

    track.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      track.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [posts]);

  const scrollToIndex = useCallback((index) => {
    const track = trackRef.current;
    if (!track) return;
    const item = track.querySelectorAll('.featured-carousel-item')[index];
    if (!item) return;
    track.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
  }, []);

  // Autoplay: advance one card every AUTOPLAY_DELAY_MS, wrapping back to the
  // first once past the last. Stops entirely at one post (nothing to advance
  // to) and whenever the reader is interacting with the row. Restarts from
  // zero after any manual navigation (manualNavCount changing) so a reader's
  // own choice gets the full delay before autoplay moves on, instead of
  // autoplay firing moments later on whatever was left of the old interval.
  useEffect(() => {
    if (posts.length < 2 || isPaused) return undefined;

    const id = setInterval(() => {
      setActiveIndex((current) => {
        const next = (current + 1) % posts.length;
        scrollToIndex(next);
        return next;
      });
    }, AUTOPLAY_DELAY_MS);

    return () => clearInterval(id);
  }, [posts.length, isPaused, scrollToIndex, manualNavCount]);

  if (!posts.length) return null;

  // Index-based, not a fixed scroll distance: scrollBy() has nowhere further
  // to go once the track is already at either end, so "next" on the last
  // card or "prev" on the first silently did nothing. Wrapping the index with
  // modulo and scrolling straight to that card fixes both ends at once.
  const step = (direction) => {
    const next = (activeIndex + direction + posts.length) % posts.length;
    setActiveIndex(next);
    scrollToIndex(next);
    setManualNavCount((n) => n + 1);
  };

  const pause = () => {
    // Cancel a pending resume from an earlier interaction — otherwise a
    // second quick swipe could have its pause immediately undone by the
    // first swipe's delayed resume firing right after.
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    setIsPaused(true);
  };
  // touchend fires the instant the finger lifts, but a swipe's momentum
  // scroll keeps moving — and the scroll-position listener above needs a
  // moment to catch up — for a bit after that. Resuming autoplay instantly
  // let the timer fire mid-momentum-scroll and yank the track back to a
  // stale activeIndex, fighting the reader's own swipe. This is what made
  // touch feel unreliable. Waiting out the momentum before resuming fixes it.
  const resume = () => {
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => setIsPaused(false), 600);
  };

  // Clear the pending resume if the carousel unmounts mid-swipe.
  useEffect(() => () => {
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
  }, []);

  return (
    <div
      className="featured-carousel"
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
      onTouchStart={pause}
      onTouchEnd={resume}
    >
      <div className="featured-carousel-track" ref={trackRef} role="group" aria-label={label}>
        {posts.map((post) => (
          <div className="featured-carousel-item" key={post.blogId}>
            <BlogCard blog={post} variant="default" headingLevel="h3" />
          </div>
        ))}
      </div>

      {/* Hidden until hover/focus-visible on wider screens (see App.css); on
          touch devices the swipe itself is the interaction, so these are
          decorative there and never in the tab order ahead of the cards. */}
      {posts.length > 1 && (
        <>
          <button
            type="button"
            className="featured-carousel-btn featured-carousel-btn-prev"
            aria-label="Scroll to previous featured article"
            onClick={() => step(-1)}
          >
            <span aria-hidden="true">‹</span>
          </button>
          <button
            type="button"
            className="featured-carousel-btn featured-carousel-btn-next"
            aria-label="Scroll to next featured article"
            onClick={() => step(1)}
          >
            <span aria-hidden="true">›</span>
          </button>
        </>
      )}

      {/* Dot pagination — mirrors what scroll-snap already decided, it does
          not drive the scroller itself. Only shown when there is more than
          one card, same as the arrows. */}
      {posts.length > 1 && (
        <div className="featured-carousel-dots" role="tablist" aria-label={`${label} pagination`}>
          {posts.map((post, i) => (
            <button
              key={post.blogId}
              type="button"
              role="tab"
              aria-selected={i === activeIndex}
              aria-label={`Go to featured article ${i + 1} of ${posts.length}`}
              className={`featured-carousel-dot${i === activeIndex ? ' is-active' : ''}`}
              onClick={() => {
                scrollToIndex(i);
                setManualNavCount((n) => n + 1);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default FeaturedCarousel;
